import { InsertMenu } from '@/components/tiptap/shared/insert-menu';
import {
  SectionEhrProvider,
  type SectionLinkHandler,
} from '@/components/tiptap/shared/section-ehr-provider';
import { EditorType } from '@heidi/editor/core';
import { type Editor } from '@tiptap/react';
import { forwardRef, memo } from 'react';
import { EditorTypeProvider } from './editor-type-context';
import { getInsertCommands } from './extensions/slash-command/group';
import { MarkdownNativeEditor } from './markdown-native-editor';

/**
 * The markdown-native template editor surface: the shared editor shell plus the
 * chrome only template editing has — the insert row beneath the document and
 * the EHR section-link context the structure tab maps against. The add-section
 * button joins the insert row once `AddSectionButton` completes its move to
 * `tiptap/shared` (markdown-native must not import it from vMarkdown/).
 *
 * A sibling of `MarkdownNativeEditor` rather than more optional props on it:
 * the note and document editors never show this chrome, and should not carry
 * the imports for it.
 */
export const MarkdownNativeTemplateEditor = memo(
  forwardRef<
    HTMLDivElement,
    {
      editor: Editor;
      /** Matches the legacy surface's tag so e2e selectors survive the swap. */
      testTag: string;
      className?: string;
      /** Absent on the example tab: only the structure tab maps to EHR fields. */
      onSectionLink?: SectionLinkHandler;
    }
  >(({ editor, testTag, className, onSectionLink }, ref) => (
    <EditorTypeProvider editorType={EditorType.TEMPLATE}>
      <SectionEhrProvider onSectionLink={onSectionLink}>
        <MarkdownNativeEditor
          ref={ref}
          editor={editor}
          testId={testTag}
          className={className}
          // Matches the legacy template surface, which passes
          // `enableFindReplace={false} enableSnippet={false}`.
          enableFindReplace={false}
          enableSnippet={false}
          footerComponent={
            <div className="flex items-center gap-2 pb-2 pt-4">
              <InsertMenu editor={editor} getCommands={getInsertCommands} />
            </div>
          }
        />
      </SectionEhrProvider>
    </EditorTypeProvider>
  ))
);

MarkdownNativeTemplateEditor.displayName = 'MarkdownNativeTemplateEditor';
